import { defineStore } from 'pinia'
import { useFeedsV2Store } from './feedsV2.js'
import axios from '../axios.js'
import { apiBalancedProjectionsPath, isFixtureV2Route } from '../config/balancedProjectionsApi.js'
import { filterLinesForScorableProjections } from '../utils/balancedLinesProjection.js'

const comparableSelectUrl = apiBalancedProjectionsPath('balanced-lines/select-comparable-data-or-player-line')

/** Balanced-line API row or roster row: one id for comparable requests and store lookups */
function resolveComparablePlayerId(player) {
  if (!player) return undefined
  return player.player_id ?? player.player?.player_id ?? player.player?.id
}

function resolveComparablePlayerName(player) {
  if (!player) return ''
  return player.player?.player_name ?? player.name ?? ''
}

/** JSON rows may use string or number player_id; strict === misses duplicates and leaves stale ladder rows. */
function sameComparablePlayerId(a, b) {
  if (a == null || b == null) return false
  return String(a) === String(b)
}

/** Backend JSON array of market keys to refresh (see FixtureProjectionController $statMappings) */
function parseMarketsThatNeedUpdate(row) {
  const raw = row?.markets_that_need_update
  if (raw == null || raw === '') return null
  if (Array.isArray(raw)) return raw.length ? raw : null
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw)
      return Array.isArray(p) && p.length ? p : null
    } catch {
      return null
    }
  }
  return null
}

/** Key for matching ladder rows to a client override (house vs client table). */
function clientOverrideRowKey(row) {
  if (!row || row.fixture_id == null || row.player_id == null || row.market_type == null) return null
  return `${String(row.fixture_id)}_${String(row.player_id)}_${String(row.market_type)}`
}

/**
 * For each (fixture_id, player_id, market_type) present in overrides, drop house
 * `fixturePlayerLines` rows and use the override row(s) instead so MarketCell shows client lines.
 */
function mergeFixturePlayerLinesWithClientOverrides(baseRows, overrideRows) {
  const base = Array.isArray(baseRows) ? [...baseRows] : []
  const overrides = Array.isArray(overrideRows) ? overrideRows : []
  if (!overrides.length) return base

  const overrideKeys = new Set()
  for (const o of overrides) {
    const k = clientOverrideRowKey(o)
    if (k) overrideKeys.add(k)
  }
  if (!overrideKeys.size) return base

  const filtered = base.filter((row) => {
    const k = clientOverrideRowKey(row)
    return !k || !overrideKeys.has(k)
  })
  return [...filtered, ...overrides]
}

export const useProjectionsStore = defineStore('projections', {
  state: () => ({
    config: {
      apiUrl: import.meta.env.VITE_API_URL
    },
    projections: [],
    allData: [],
    selectedLeague: null,
    fixture_published_at: null,
    fixture_suspension: false,
    milestones_only: false,
    home_team_suspension: false,
    away_team_suspension: false,
    updatesFromWebSocket: [],
    fixture_balanced_lines_column_suspensions: [],
    feedPricingMarkets: [],
    // Fixture-level Feed mode switch (GET feed-pricing/mode); null until loaded / older backend.
    feedMode: null,
    market_columns: {
      points: 'Points',
      total_rebounds: 'Total Rebounds',
      assists: 'Assists',
      three_point_field_goal: '3-Point Field Goal',
      pra: 'PRA',
    },
    comparableDataPlayerLinesUrl: comparableSelectUrl,
    comparableDataUrl: comparableSelectUrl,
    selectedComparableDataUrl: '',
    allMarkets: [
      'points',
      'total_rebounds',
      'assists',
      'three_point_field_goal',
      'points_three_point_field_goal',
      'turnovers',
      'blocks',
      'steals',
      'pra',
      'pr',
      'pa',
      'bs',
      'ra',
    ],
    triggeredIDs: [],
    playersWithMarkets: [], 
    playersThatNeedsUpdateIDs: [],
    balancedLinesMergedTeams: [],
    globalSearchPopup: false,
    autoProjectionPopup: false,
    loading: false,
    error: null,
    selectedLeagues: [],
    selectedSeason: [],
    perProjectionFilters: {
      leagues: [],
      seasons: []
    },
    projectionsData: [],
    balancedLinesData: [],
    homeAndAwayTeamsTogether: [],
    fixtureProjectionsConfirmationLoadingStatus: false,
    balancedLinesProbabilitiesAndOdds: [],
    balancedLinesMetadata: [],
    marketFilters: [],
    debug_player_market_data: [],
    saveSuccess: false,
    saveError: null,
    playerMarketCounters: [],
    /** In-flight MarketCell local overlays (spinner not tied to playersWithMarkets batch). */
    marketCellLocalOverlayCount: 0,
    fixtureBalanceLines:[],
    
    // WebSocket state
    ws: null,
    wsConnected: false,
    wsConnecting: false,
    wsReconnectAttempts: 0,
    wsMaxReconnectAttempts: 5,
    wsReconnectInterval: null,
    wsUrl: 'wss://console.oddscomposer.io/ws',
    wsMessages: [],
    wsLastMessage: null,
    showMonitoringToolMapping: false,
  
  }),

  getters: {
    // All trader views consume the same fixed rows with their selected model's
    // prices. Stored projection rows remain available for updates and fallback.
    pricedPlayerLines: (state) => useFeedsV2Store().priceLines(
      state.balancedLinesProbabilitiesAndOdds,
      state.projectionsData?.fixture_id ?? state.projectionsData?.id
    ),
    getIsBalancedLine: (state) => (playerId, marketType) => {
      // Find all lines for this player and market

      // console.log('getIsBalancedLine', playerId, marketType)
      const playerLines = useProjectionsStore().pricedPlayerLines.filter(
        (item) => item.market_type === marketType && sameComparablePlayerId(item.player_id, playerId)
      )

      if (!playerLines.length) return null

      // Prefer the line where is_balanced is true (should be only one, but fallback if not)
      const balancedLine = playerLines.find(line => line.is_balanced === true || line.is_balanced === 1)
      if (balancedLine) return balancedLine

      // Fallback: previous logic (closest to balanced probabilities)
      return playerLines.reduce((closest, current) => {
        const currentDiff = Math.abs(current.balance_line_over_probability * 100 - current.balance_line_under_probability * 100)
        const closestDiff = Math.abs(closest.balance_line_over_probability * 100 - closest.balance_line_under_probability * 100)
        return currentDiff < closestDiff ? current : closest
      })
    },
    getProjections: (state) => state.projections,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    getFilterLeagues: (state) => state.perProjectionFilters.leagues,
    getFilterSeasons: (state) => state.perProjectionFilters.seasons,
    getFilters: (state) => state.perProjectionFilters,
    
    // WebSocket getters
    isWebSocketConnected: (state) => state.wsConnected,
    isWebSocketConnecting: (state) => state.wsConnecting,
    getWebSocketMessages: (state) => state.wsMessages,
    getLastWebSocketMessage: (state) => state.wsLastMessage,

    /** Rows in the post–fetch-balanced-lines comparable batch (player × market). */
    marketCellsComparableBatchTotal: (state) => state.playersWithMarkets.length,
    marketCellsComparableBatchLoadingCount: (state) =>
      state.playersWithMarkets.filter((p) => p.is_loading).length,
    /** True after a batch was queued and every entry finished (is_loading cleared). */
    allComparableBatchMarketCellsLoaded: (state) =>
      state.playersWithMarkets.length > 0 &&
      state.playersWithMarkets.every((p) => !p.is_loading),
    anyMarketCellOverlayActive: (state) =>
      state.playersWithMarkets.some((p) => p.is_loading) ||
      state.marketCellLocalOverlayCount > 0,
    allMarketCellOverlaysIdle: (state) =>
      !state.playersWithMarkets.some((p) => p.is_loading) &&
      state.marketCellLocalOverlayCount === 0,
    
    getPlayerPerThirtyValue: (state) => (playerId, statKey) => {
      const playerProjection = state.projectionsData?.fixture_projections?.find(p => p.player_id === playerId)
      if (!playerProjection) return { value: '—', bg: 'bg-gray-200', text: 'text-black text-[12px]' }
      return { value: playerProjection[`per_thirty_${statKey}`], bg: 'bg-gray-200', text: 'text-black text-[12px]' }
    },
    getRowProjectionValueFromPerThirty: (state) => (playerId, statKey) => {
      const playerProjection = state.projectionsData?.fixture_projections?.find(p => p.player_id === playerId)
      if (!playerProjection) return null

      // Get per30 value based on priority: trim > manual > auto > original
      let per30Value = null
      if (playerProjection[`${statKey}_trim`] != null && playerProjection[`${statKey}_trim`] !== 0) {
        per30Value = playerProjection[`${statKey}_trim`]
      } else if (playerProjection[`${statKey}_manual`] != null && playerProjection[`${statKey}_manual`] !== '' && playerProjection[`${statKey}_manual`] > 0) {
        per30Value = playerProjection[`${statKey}_manual`]
      } else if (playerProjection[`${statKey}_auto`] != null && playerProjection[`${statKey}_auto`] !== '' && playerProjection[`${statKey}_auto`] > 0) {
        per30Value = playerProjection[`${statKey}_auto`]
      } else if (playerProjection[`${statKey}_original`] != null) {
        per30Value = playerProjection[`${statKey}_original`]
      }

      if (per30Value === null) return null

      // Get projected minutes from store (this will be reactive)
      const projectedMinutes = playerProjection.projected_minutes || 0
      if (!projectedMinutes) return null

      // Calculate raw value using formula: per30/30 * projected_minutes
      const rawValue = (Number(per30Value) / 30) * Number(projectedMinutes)
      
      return isNaN(rawValue) ? null : rawValue
    },

    // New getter for formatted projection value
    getFormattedProjectionValue: (state) => (playerId, statKey, teamType) => {
      // Handle both single ID and array of IDs
      const playerIds = Array.isArray(playerId) ? playerId : [playerId]
      
      // If array of IDs, sum up the values
      if (playerIds.length > 1) {
        const totalValue = playerIds.reduce((sum, id) => {
          const playerProjection = state.projectionsData?.fixture_projections?.find(p => p.player_id === id)
          if (!playerProjection) return sum

          const per30Value = (() => {
            const trimValue = playerProjection[`${statKey}_trim`]
            const manualValue = playerProjection[`${statKey}_manual`]
            const autoValue = playerProjection[`${statKey}_auto`]
            const originalValue = playerProjection[`${statKey}_original`]

            if (trimValue != null && trimValue !== 0) return trimValue
            if (manualValue != null && manualValue !== '' && manualValue > 0) return manualValue
            if (autoValue != null && autoValue !== '' && autoValue > 0) return autoValue
            if (originalValue != null) return originalValue
            return null
          })()

          if (per30Value === null) return sum

          const projectedMinutes = playerProjection.projected_minutes || 0
          if (!projectedMinutes) return sum

          const rawValue = (Number(per30Value) / 30) * Number(projectedMinutes)
          return sum + (isNaN(rawValue) ? 0 : rawValue)
        }, 0)

        return totalValue === 0 ? '—' : totalValue.toFixed(2)
      }

      // Single ID logic (original implementation)
      const playerProjection = state.projectionsData?.fixture_projections?.find(p => p.player_id === playerId)
      if (!playerProjection) return '—'

      const per30Value = (() => {
        const trimValue = playerProjection[`${statKey}_trim`]
        const manualValue = playerProjection[`${statKey}_manual`]
        const autoValue = playerProjection[`${statKey}_auto`]
        const originalValue = playerProjection[`${statKey}_original`]

        if (trimValue != null && trimValue !== 0) return trimValue
        if (manualValue != null && manualValue !== '' && manualValue > 0) return manualValue
        if (autoValue != null && autoValue !== '' && autoValue > 0) return autoValue
        if (originalValue != null) return originalValue
        return null
      })()

      if (per30Value === null) return '—'

      const projectedMinutes = playerProjection.projected_minutes || 0
      if (!projectedMinutes) return '—'

      const rawValue = (Number(per30Value) / 30) * Number(projectedMinutes)
      
      return isNaN(rawValue) ? '—' : rawValue.toFixed(2)
    },

    // Getter for projected minutes from store
    getPlayerProjectedMinutes: (state) => (playerId) => {
      const playerProjection = state.projectionsData?.fixture_projections?.find(p => p.player_id === playerId)
      return playerProjection?.projected_minutes || 0
    },
  },

  actions: {

    async fillPlayerLines(fixtureId) {

      try {
        const response = await axios.get(`/api/player-lines/fill-player-lines/${fixtureId}`)

        // this.balancedLinesData = response.data.fixtureBalancedLines
        this.balancedLinesProbabilitiesAndOdds = response.data.fixturePlayerLines
        // this.fillPlayerLines = response.data
      } catch (error) {
        console.error('Error filling player lines:', error)
      }
      // this.balancedLinesData = this.balancedLinesData.filter(item => item.fixture_id === fixtureId)
    },

    // async updateFixtureBalancedLinesSuspnesionsFromAPI() {
      
     
    // },

    async publishBalancedLines(fixtureId) {
      console.log('publishBalancedLines', fixtureId)
      try {
        const response = await axios.post(apiBalancedProjectionsPath(`balanced-lines/publish/${fixtureId}`))
        console.log('publishBalancedLines response', response)
      } catch (error) {
        console.error('publishBalancedLines error', error)
      }
    },

    // WebSocket Actions
    initWebSocket() {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected')
        return
      }
      
      this.connectWebSocket()
    },

    async connectWebSocket() {
      if (this.wsConnecting || (this.ws && this.ws.readyState === WebSocket.CONNECTING)) {
        console.log('WebSocket connection already in progress')
        return
      }

      this.wsConnecting = true
      this.wsConnected = false

      try {
        // Get authorization token from userStore
        const { useUserStore } = await import('./userStore.js')
        const userStore = useUserStore()
        
        // Add authorization as query parameter (since browser WebSocket doesn't support custom headers)
        let wsUrl = this.wsUrl
        if (userStore.token) {
          const separator = wsUrl.includes('?') ? '&' : '?'
          wsUrl += `${separator}authorization=${encodeURIComponent(userStore.token)}`
        }
        
        console.log(`Connecting to WebSocket: ${wsUrl}`)
        this.ws = new WebSocket(wsUrl)

        this.ws.onopen = async (event) => {
          console.log('WebSocket connected successfully')
          this.wsConnected = true
          this.wsConnecting = false
          this.wsReconnectAttempts = 0
          
          // Clear any reconnect interval
          if (this.wsReconnectInterval) {
            clearInterval(this.wsReconnectInterval)
            this.wsReconnectInterval = null
          }

          // Send subscription message
          this.sendWebSocketMessage({
            type: 'subscribe',
            channel: 'projections',
            timestamp: new Date().toISOString()
          })
        }

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            // console.log('WebSocket message received:', message)
            
            this.wsLastMessage = message
            this.wsMessages.push({
              ...message,
              receivedAt: new Date().toISOString()
            })

            // console.log('WebSocket message received:', message)

            // Safely update balancedLinesProbabilitiesAndOdds only if message.fixturePlayerLines is valid
            if (
              message.fixturePlayerLines &&
              message.fixturePlayerLines.fixture_id != null &&
              message.fixturePlayerLines.player_id != null &&
              message.fixturePlayerLines.market_type != null
            ) {
              const wsRow = message.fixturePlayerLines
              const overrides = this.balancedLinesData?.fixturePlayerLinesClientOverride || []
              const wsKey = clientOverrideRowKey(wsRow)
              const hasClientOverride =
                wsKey &&
                Array.isArray(overrides) &&
                overrides.some((o) => clientOverrideRowKey(o) === wsKey)
              if (!hasClientOverride) {
                this.balancedLinesProbabilitiesAndOdds = this.balancedLinesProbabilitiesAndOdds?.filter(
                  (item) =>
                    item.fixture_id !== wsRow.fixture_id ||
                    item.player_id !== wsRow.player_id ||
                    item.market_type !== wsRow.market_type
                )
                this.balancedLinesProbabilitiesAndOdds.push(wsRow)
                this.retainScorableBalancedLines()
              }
            }

            this.updatesFromWebSocket.push(message.fixturePlayerLines)
            // this.balancedLinesProbabilitiesAndOdds = message.data.allLines
            // console.log('all probabilities and odds from websocket', message.data.allLines)

            // Keep only last 100 messages
            if (this.wsMessages.length > 100) {
              this.wsMessages = this.wsMessages.slice(-100)
            }

            // Handle different message types
            this.handleWebSocketMessage(message)
            
          } catch (error) {
            console.error('Error parsing WebSocket message:', error)
          }
        }

        this.ws.onclose = (event) => {
          // console.log('WebSocket disconnected:', event.code, event.reason)
          this.wsConnected = false
          this.wsConnecting = false
          
          // Only attempt reconnection if it wasn't a manual disconnect
          if (event.code !== 1000 && this.wsReconnectAttempts < this.wsMaxReconnectAttempts) {
            this.attemptReconnect()
          }
        }

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error)
          this.wsConnecting = false
          this.wsConnected = false
          
          if (this.wsReconnectAttempts < this.wsMaxReconnectAttempts) {
            this.attemptReconnect()
          }
        }

      } catch (error) {
        console.error('Failed to create WebSocket connection:', error)
        this.wsConnecting = false
        this.wsConnected = false
      }
    },

    handleWebSocketMessage(message) {
      switch (message.type) {
        case 'welcome':
          // console.log('Received welcome message:', message.message)
          break
          
        case 'subscription_success':
          console.log('WebSocket subscription successful:', message)
          break
          
        case 'job_queued':
          console.log('Comparable data request queued:', message)
          // Show loading state - job is now processing in background
          break
        
        case 'client-message':

          // this.balancedLinesProbabilitiesAndOdds.push(...message.data.data.allLines)
          // console.log('all probabilities and odds from websocket job', message.data.data.allLines)
          // if (message.data && message.data.allLines) {
          //   console.log('all probabilities and odds from websocket job', message.data.allLines)
          // }

        // break
        //   // Update other store state with the received data
        //   console.log('Received client message:', message)
        break
        case 'comparable_data_response_laravel_queue':
          console.log('Received comparable data response:', message)
          this.handleComparableDataResponse({data: message})
          break 
        case 'comparable_data_response':
          // console.log('Received comparable data response:', message)
          // this.handleComparableDataResponse({data: message})

          // console.log('Received comparable data response:', message)
          // Handle the final comparable data response from the job
         
          break
          
        case 'projection_update':
          // console.log('Received projection update:', message)
          // Handle projection updates here
          if (message.data && message.data.fixture_id) {
            this.handleProjectionUpdate(message.data.data.allLines)
          }
          break
          
        case 'balanced_lines_update':
          // console.log('Received balanced lines update:', message)
          // Handle balanced lines updates here
          if (message.data) {
            this.handleBalancedLinesUpdate(message.data)
          }
          break
        case 'feed_pricing_update':
          if (message.data) {
            this.applyFeedPricingPayload(message.data)
          }
          break
          
        case 'echo':
          // console.log('Received echo:', message)
          break
          
        case 'broadcast':
          // console.log('Received broadcast:', message)
          break
          
        case 'client-message':
          // Handle messages sent directly to this client from Laravel
          if (message.data && message.data.type) {
            // Re-process the nested message
            this.handleWebSocketMessage(message.data)
          }
          break
          
        case 'error':
          console.error('WebSocket server error:', message.message)
          break
          
        default:
          // console.log('Unknown message type:', message.type)
      }
    },

    handleProjectionUpdate(data) {
      // Update projections data if it matches current fixture
      if (this.projectionsData?.id === data.fixture_id) {
        if (data.fixture_projections) {
          this.projectionsData.fixture_projections = data.fixture_projections
          this.projectionsData.home_team_roster['fixture_projections'] = data.fixture_projections.filter(p => p.team_type === 1)
          this.projectionsData.away_team_roster['fixture_projections'] = data.fixture_projections.filter(p => p.team_type === 2)
        }
      }
    },

    handleBalancedLinesUpdate(data) {
      // Update balanced lines data
      if (data.balanced_lines) {
        this.balancedLinesData = data.balanced_lines
        this.balancedLinesMergedTeams = [...data.balanced_lines.home_team, ...data.balanced_lines.away_team]
      }
      
      if (data.probabilities_and_odds) {
        this.balancedLinesProbabilitiesAndOdds = data.probabilities_and_odds
        this.retainScorableBalancedLines()
      }
    },

    handleComparableDataResponse(message) {
      // Handle comparable data response from job
      console.log('handleComparableDataResponse', message)
      // if (!message.data) return
      
      const { player_id, market_type, fixture_id } = message.data
      const responseData = message.data
      
      // Remove triggered ID
      this.triggeredIDs = this.triggeredIDs.filter(item => 
        !(item.player_id === player_id && (item.market_type === market_type))
      )
      
      // Update balanced lines probabilities and odds
      if (responseData.allLines && responseData.allLines.length > 0) {
        // Filter out existing lines for this market/player combination
        this.balancedLinesProbabilitiesAndOdds = this.balancedLinesProbabilitiesAndOdds.filter(
          item => !(item.market_type === market_type && item.player_id === player_id)
        )
        
        // Add new lines from response
        this.balancedLinesProbabilitiesAndOdds = [
          ...this.balancedLinesProbabilitiesAndOdds,
          ...responseData.allLines,
        ]
        this.retainScorableBalancedLines()
      }
      
      // Update player market counters
      const existingIndex = this.playerMarketCounters.findIndex(
        item => item.player_id === player_id && item.market_id === market_type
      )
      
      if (existingIndex !== -1) {
        this.playerMarketCounters[existingIndex].counter = responseData.comparableDataCount
      } else {
        this.playerMarketCounters.push({
          player_id: player_id,
          market_id: market_type,
          counter: responseData.comparableDataCount
        })
      }
      
      // Update filters
      if (responseData.filters) {
        const existingFilterIndex = this.marketFilters.findIndex(filter => 
          filter.market_type === responseData.filters.market_type && 
          filter.player_id === responseData.filters.player_id && 
          filter.fixture_id === responseData.filters.fixture_id
        )
        
        if (existingFilterIndex === -1) {
          this.marketFilters.push(responseData.filters)
        } else {
          this.marketFilters[existingFilterIndex] = responseData.filters
        }
      }
      
      // Update metadata
      if (responseData.balancedLine) {
        const existingMetadataIndex = this.balancedLinesMetadata.findIndex(
          item => item.player_id === responseData.balancedLine.player_id
        )
        
        if (existingMetadataIndex !== -1) {
          this.balancedLinesMetadata[existingMetadataIndex] = responseData.balancedLine
        } else {
          this.balancedLinesMetadata.push(responseData.balancedLine)
        }
      }
      
      // Update debug data
      if (responseData.query_debug) {
        const existingDebugIndex = this.debug_player_market_data.findIndex(
          item => item.player_id === player_id && item.market_id === market_type
        )
        
        if (existingDebugIndex !== -1) {
          this.debug_player_market_data[existingDebugIndex].debug = responseData.query_debug
        } else {
          this.debug_player_market_data.push({
            player_id: player_id,
            market_id: market_type,
            debug: responseData.query_debug
          })
        }
      }
      
      console.log('Comparable data response processed successfully')
    },

    sendWebSocketMessage(message) {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify(message))
          // console.log('WebSocket message sent:', message)
        } catch (error) {
          console.error('Failed to send WebSocket message:', error)
        }
      } else {
        console.warn('WebSocket not connected. Message not sent:', message)
      }
    },

    attemptReconnect() {
      if (this.wsReconnectAttempts >= this.wsMaxReconnectAttempts) {
        // console.log('Max reconnection attempts reached')
        return
      }

      this.wsReconnectAttempts++
      const delay = Math.min(1000 * Math.pow(2, this.wsReconnectAttempts), 30000) // Exponential backoff, max 30s
      
      console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.wsReconnectAttempts}/${this.wsMaxReconnectAttempts})`)
      
      this.wsReconnectInterval = setTimeout(() => {
        this.connectWebSocket()
      }, delay)
    },

    disconnectWebSocket() {
      if (this.wsReconnectInterval) {
        clearInterval(this.wsReconnectInterval)
        this.wsReconnectInterval = null
      }

      if (this.ws) {
        this.ws.close(1000, 'Manual disconnect')
        this.ws = null
      }
      
      this.wsConnected = false
      this.wsConnecting = false
      this.wsReconnectAttempts = 0
    },

    clearWebSocketMessages() {
      this.wsMessages = []
      this.wsLastMessage = null
    },

    async fetchFixtureBalanceLines(fixtureId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`/api/balanced-lines/fixture/`)
        this.fixtureBalanceLines = response.data
      } catch (error) {
        this.error = error.message || 'Failed to fetch fixture balance lines'
        console.error('Error fetching fixture balance lines:', error)
      } finally {
        this.loading = false
      }
    },
    addTriggeredIDWIthAllMarkets(playerId) {
      this.allMarkets.forEach(marketId => {
        this.triggeredIDs.push({
          player_id: playerId,
          market_type: marketId
        })
      })
    },
    addSingleTriggeredIDForceUpdate(payload) {
      console.log('addSingleTriggeredIDForceUpdate', payload)
      this.selectedComparableDataUrl = this.comparableDataUrl
      this.allMarkets.forEach(marketId => {
        this.triggeredIDs.push({
          player_id: payload.player_id,
          market_type: marketId,
          ...payload
        })
      })
    },
    addSingleTriggeredID(playerId, marketId, payload) {
      if(marketId === 'refresh') {
        this.selectedComparableDataUrl = this.comparableDataUrl
        this.allMarkets.forEach(marketId => {
          this.triggeredIDs.push({
            player_id: playerId,
            market_type: marketId,
            ...payload,
          })
        })
      } else if (marketId === 'all') {
        this.selectedComparableDataUrl = this.comparableDataUrl
        this.allMarkets.forEach(marketId => {
          this.triggeredIDs.push({
            player_id: playerId,
            market_type: marketId,
            ...payload
          })
        })
      } else {
        this.selectedComparableDataUrl = this.comparableDataPlayerLinesUrl
        this.triggeredIDs.push({
          player_id: playerId,
          market_type: marketId,
          ...payload
        })
      }
    },
    incrementMarketCellLocalOverlay() {
      this.marketCellLocalOverlayCount++
    },
    decrementMarketCellLocalOverlay() {
      this.marketCellLocalOverlayCount = Math.max(0, this.marketCellLocalOverlayCount - 1)
    },
    retainScorableBalancedLines() {
      if (!isFixtureV2Route()) return
      this.balancedLinesProbabilitiesAndOdds = filterLinesForScorableProjections(
        this.balancedLinesProbabilitiesAndOdds,
        this.balancedLinesMergedTeams
      )
    },
    async fetchBalancedLines(fixtureId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(apiBalancedProjectionsPath(`balanced-lines/both/${fixtureId}`))
        const data = response.data || {}
        const homeTeam = Array.isArray(data.home_team) ? data.home_team : []
        const awayTeam = Array.isArray(data.away_team) ? data.away_team : []
        this.balancedLinesData = data
        this.balancedLinesMergedTeams = [...homeTeam, ...awayTeam]
        this.balancedLinesProbabilitiesAndOdds = mergeFixturePlayerLinesWithClientOverrides(
          data.fixturePlayerLines || [],
          data.fixturePlayerLinesClientOverride || []
        )
        this.retainScorableBalancedLines()
        await this.fetchFeedPricingState(fixtureId)

        this.playersThatNeedsUpdateIDs = this.balancedLinesMergedTeams.filter(item => item.needs_update ==1)
        this.selectedComparableDataUrl = this.comparableDataUrl

        // add also to balancedLinesMergedTeams the fiedl is_loading = true
        // this.balancedLinesMergedTeams.forEach(player => {
          
        // })
        this.playersWithMarkets = []
        // Fixture V2 may display persisted production-compatible probability rows,
        // but must never fall back to the legacy calculation when rows are stale.
        if (!isFixtureV2Route()) {
          this.playersThatNeedsUpdateIDs.forEach((lineRow) => {
            const pid = resolveComparablePlayerId(lineRow)
            if (pid == null) return
            const fromServer = parseMarketsThatNeedUpdate(lineRow)
            const marketsToFetch = fromServer && fromServer.length ? fromServer : this.allMarkets

            marketsToFetch.forEach((marketId) => {
              this.playersWithMarkets.push({
                player_id: pid,
                market_type: marketId,
                is_loading: true,
              })
              this.getComparableDataWithUrl(lineRow, marketId, fixtureId, true, true)
            })
          })
        }

        // console.log('listPlayersThatNeedsUpdate', this.listPlayersThatNeedsUpdate)

      } catch (error) {
        this.error = error.message || 'Failed to fetch balanced lines'
        console.error('Error fetching balanced lines:', error)
      } finally {
        this.loading = false

        // this.triggeredIDs.push(...this.playersThatNeedsUpdateIDs.map(player => ({
        //   player_id: player.player_id,
        //   market_type: 'all',
        //   force_update: true
        // })))

        // console.log('triggeredIDs', this.triggeredIDs)
      }
    },

    async saveToBalancedLines() {
      this.fixtureProjectionsConfirmationLoadingStatus = true
        console.log("saveToBalancedLines", this.projectionsData.fixture_projections)
        const data = {
          fixture_id: this.projectionsData.id,
          // fixture_projections: this.projectionsData.fixture_projections
        }
        
        try {
          const response = await axios.post(apiBalancedProjectionsPath('projections/save-to-balanced-lines'), data)
          console.log("response", response)
          this.saveSuccess = true
          this.saveError = null
        } catch (error) {
          console.error(error)
          this.fixtureProjectionsConfirmationLoadingStatus = false
        } finally {
          this.fixtureProjectionsConfirmationLoadingStatus = false
        }
      },

    async undoTrim(statKey, teamType) {
      console.log("teamType", teamType)
      if (teamType === 'A') {
        teamType = 1
      } else {
        teamType = 2
      }
      console.log("projectionsData", this.projectionsData)
      const trimmedValuesWithPlayerIDs = []
      this.projectionsData.fixture_projections.filter(projection => projection.team_type === teamType).forEach(projection => {
        trimmedValuesWithPlayerIDs.push({
          playerId: projection.player_id,
          trimValue: 0
        })
      })

      try {
        const response = await axios.post(apiBalancedProjectionsPath('projections/remove-trim-values'), {
          fixture_id: this.projectionsData.id,
          statKey: statKey,
          trimmedValuesWithPlayerIDs: trimmedValuesWithPlayerIDs
        })

        this.projectionsData.fixture_projections.filter(projection => projection.team_type === teamType).forEach(projection => {
          const matchingProjection = response.data.fixture_projections.find(p => p.player_id === projection.player_id)
          if (matchingProjection) {
            projection[`${statKey}_trim`] = matchingProjection[`${statKey}_trim`] || 0
          }
        })
      } catch (error) {
        console.error(error)
      }
    },
    async saveTrimValues(statKey, trimValue, teamType) {  
      const trimmedValuesWithPlayerIDs = []
      console.log("teamType", teamType)
      if (teamType === 'A') {
        teamType = 1
      } else {
        teamType = 2
      }
        console.log("projectionsData", this.projectionsData)
        // this.projectionsData.fixture_projections.forEach(projection => {
        //   const allPlayerIds = this.projectionsData.fixture_projections.filter(projection => projection.team_type === teamType).map(projection => projection.player_id)
        // console.log('allPlayerIds', allPlayerIds)
        // const newProjectionValue = this.getRowProjectionValueFromPerThirty(projection.player_id, statKey, teamType)/ this.getFormattedProjectionValue(allPlayerIds, statKey, teamType) * trimValue

        // console.log('from 30 :', this.getRowProjectionValueFromPerThirty(projection.player_id, statKey, teamType))
        // console.log('market total:', this.getFormattedProjectionValue(allPlayerIds, statKey), 'trim value:', trimValue)

        // const newTrimPer30Value = newProjectionValue * 30 / projection.projected_minutes

        // trimmedValuesWithPlayerIDs.push({
        //   playerId: projection.player_id,
        //   trimValue: newTrimPer30Value
        // })

        this.projectionsData.fixture_projections.filter(projection => projection.team_type === teamType).forEach(projection => {
          const allPlayerIds = this.projectionsData.fixture_projections.filter(projection => projection.team_type === teamType).map(projection => projection.player_id)
          console.log('allPlayerIds', allPlayerIds)
          const newProjectionValue = this.getRowProjectionValueFromPerThirty(projection.player_id, statKey, teamType)/ this.getFormattedProjectionValue(allPlayerIds, statKey, teamType) * trimValue
          console.log('from 30 :', this.getRowProjectionValueFromPerThirty(projection.player_id, statKey, teamType))
          console.log('market total:', this.getFormattedProjectionValue(allPlayerIds, statKey), 'trim value:', trimValue)

          const newTrimPer30Value = newProjectionValue * 30 / projection.projected_minutes

        
        console.log('New projection value:', newProjectionValue)
        console.log('New trim per30 value:', newTrimPer30Value)

        trimmedValuesWithPlayerIDs.push({
          playerId: projection.player_id,
          trimValue: newTrimPer30Value
        })
        // projection[`${statKey}_trim`] = newProjectionValue
      })

      try {
        const response = await axios.post(apiBalancedProjectionsPath('projections/save-trim-values'), {
          fixture_id: this.projectionsData.id,
          statKey: statKey,
          trimmedValuesWithPlayerIDs: trimmedValuesWithPlayerIDs
        })

        console.log('statKey', statKey)
        this.projectionsData.fixture_projections.forEach(projection => {
          console.log('projection live', projection)
          const matchingProjection = response.data.fixture_projections.find(p => p.player_id === projection.player_id)
          if (matchingProjection) {
            projection[`${statKey}_trim`] = matchingProjection[`${statKey}_trim`] || 0
          }
        })
      } catch (error) {
        console.error(error)
      }
  
      // const playerProjection = this.projectionsData?.fixture_projections?.find(p => p.player_id === playerId)
      // if (playerProjection) {
      //   playerProjection[`${statKey}_trim`] = trimValue
      //   console.log('Trim value saved:', trimValue)
      // }
    },
    // Filter management actions
    setFilterLeagues(leagues) {
      this.perProjectionFilters.leagues = leagues
    },
    
    setFilterSeasons(seasons) {
      this.perProjectionFilters.seasons = seasons
    },
    
    updateFilters({ leagues, seasons }) {
      this.perProjectionFilters.leagues = leagues
      this.perProjectionFilters.seasons = seasons
    },
    
    initializeFilters() {
      // Initialize with all leagues and the latest season by default
      const LEAGUES = [
        'basketball-champions-league', 'eurocup', 'euroleague', 'fiba-europe-cup',
        'finland-korisliiga', 'france-betclic-elite', 'germany-easycredit-bb',
        'greece-heba-a1', 'italy-lba-serie-a', 'japan-b1-league',
        'latvian-estonian-basketball-league', 'latvian-estonian-basketball-league-po',
        'lithuania-betsafe-lkl', 'nba', 'poland-plk', 'spain-liga-endesa',
        'sweden-basketligan', 'turkey-bsl', 'wnba'
      ]
      const SEASONS = ['2019-2020', '2020-2021', '2021-2022', '2022-2023', '2023-2024', '2024-2025']
      
      this.perProjectionFilters.leagues = LEAGUES
      this.perProjectionFilters.seasons = [SEASONS[SEASONS.length - 1]] // Latest season
    },

    async createProjectionsBasedOnFiltersAndRoster(payload) {
      // Implement create projections functionality
      try {
        const response = await axios.post(`/api/projections/create-projections`, {
          fixture_id: payload.fixture_id,
          filters: this.perProjectionFilters
        })
        console.log(this.projectionsData)
        this.projectionsData.fixture_projections = response.data.fixture_projections
        this.projectionsData.home_team_roster['fixture_projections'] = response.data.fixture_projections.filter(p => p.team_type === 1)
        this.projectionsData.away_team_roster['fixture_projections'] = response.data.fixture_projections.filter(p => p.team_type === 2)
        console.log(response)
      } catch (error) {
        console.error(error)
      }
    },
    async fetchProjectionsDataByFixtureId(fixtureId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`/api/fixtures/get-fixture-by-id/${fixtureId}`)
        this.projectionsData = response.data
        this.projectionsData.home_team_roster['fixture_projections'] = response.data.fixture_projections.filter(p => p.team_type === 1)
        this.projectionsData.away_team_roster['fixture_projections'] = response.data.fixture_projections.filter(p => p.team_type === 2)
        this.fixture_suspension = response.data.fixture_suspension
        this.fixture_balanced_lines_column_suspensions = response.data.fixture_balanced_lines_column_suspensions
        this.home_team_suspension = response.data.home_team_suspension
        this.away_team_suspension = response.data.away_team_suspension
        this.fixture_published_at = response.data.published_at
      } catch (error) {
        console.error(error)
      }
      finally {
        this.loading = false
      }
    },
    async removeProjections(fixtureId) {
      try {
        const response = await axios.post(`/api/projections/remove-projections/${fixtureId}`)
        this.projectionsData = []
      } catch (error) {
        console.error(error)
      }
    },

    // Update projected minutes in store
    updatePlayerProjectedMinutes(playerId, projectedMinutes) {
      if (this.projectionsData?.fixture_projections) {
        const playerProjection = this.projectionsData.fixture_projections.find(p => p.player_id === playerId)
        if (playerProjection) {
          playerProjection.projected_minutes = projectedMinutes
        }
      }
    },

    // Update player settlement data in store
    updatePlayerSettlementData(playerId, settlementData) {
      console.log('🔄 Updating player settlement data in store:', { playerId, settlementData })
      
      // Update home team players
      if (this.balancedLinesData?.home_team) {
        const homePlayer = this.balancedLinesData.home_team.find(p => (p.id || p.player_id) === playerId)
        if (homePlayer) {
          homePlayer.scraped_player_name = settlementData.scraped_player_name
          homePlayer.settlement_stats = settlementData.settlement_stats
          console.log('✅ Updated home team player:', homePlayer.player?.player_name)
          return
        }
      }
      
      // Update away team players
      if (this.balancedLinesData?.away_team) {
        const awayPlayer = this.balancedLinesData.away_team.find(p => (p.id || p.player_id) === playerId)
        if (awayPlayer) {
          awayPlayer.scraped_player_name = settlementData.scraped_player_name
          awayPlayer.settlement_stats = settlementData.settlement_stats
          console.log('✅ Updated away team player:', awayPlayer.player?.player_name)
          return
        }
      }
      
      console.warn('⚠️ Player not found in store for settlement update:', playerId)
    },

    async createProjections(scheduleId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post(`${import.meta.env.VITE_LEGACY_API_URL || 'https://api.hel-rob.oddscomposer.io'}/api/markets/generate-fixture-projections`, {
          scheduleId,
          season: []
        })

        if (!response.data || !response.data.team1 || !response.data.team2) {
          throw new Error('Invalid API response format')
        }

      } catch (error) {
        this.error = error.message || 'Failed to create projections'
        console.error('Error creating projections:', error)
      } finally {
        this.loading = false
      }
    },

    updateProjection(id, data) {
      this.projections = this.projections.map(p => p.id === id ? { ...p, ...data } : p)
    },
    async fetchProjections(scheduleId) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post(`${import.meta.env.VITE_LEGACY_API_URL || 'https://api.hel-rob.oddscomposer.io'}/api/markets/get-team-players-from-roster-template`, {
          scheduleId,
          season: []
        })

        if (!response.data || !response.data.team1 || !response.data.team2) {
          throw new Error('Invalid API response format')
        }

        // Transform and combine players from both teams
        const team1Players = (response.data.team1.players || []).map(player => ({
          id: player.id,
          number: player.player?.player_id || '',
          player: player.player,
          name: player.player_name || player.name || player.player?.name || 'Unknown Player',
          position: player.player?.position || '-',
          team: response.data.team1.team_name,
          isHome: true,
          seasonAvgMinutes: player.minutes || '0',
          projectedMinutes: player.minutes || '0',
          points: { 
            value: player.player?.savedProjections?.projected_points || player.points || '0', 
            odds: '-' 
          },
          rebounds: { 
            value: player.total_rebounds || player.rebounds || '0', 
            odds: '-' 
          },
          assists: { 
            value: player.assists || '0', 
            odds: '-' 
          },
          threePointers: { 
            value: player.three_points_made_attempted?.split('-')[0] || '0', 
            odds: '-' 
          },
          pra: (
            Number(player.points || 0) + 
            Number(player.total_rebounds || player.rebounds || 0) + 
            Number(player.assists || 0)
          ).toFixed(2),
          turnovers: { 
            value: player.turnovers || '0', 
            odds: '-' 
          },
          blocks: { 
            value: player.blocks || '0', 
            odds: '-' 
          },
          steals: { 
            value: player.steals || '0', 
            odds: '-' 
          }
        }))

        const team2Players = (response.data.team2.players || []).map(player => ({
          id: player.id,
          number: player.player?.player_id || '',
          player: player.player,
          name: player.player_name || player.name || player.player?.name || 'Unknown Player',
          position: player.player?.position || '-',
          team: response.data.team2.team_name,
          isHome: false,
          seasonAvgMinutes: player.minutes || '0',
          projectedMinutes: player.minutes || '0',
          points: { 
            value: player.player?.savedProjections?.projected_points || player.points || '0', 
            odds: '-' 
          },
          rebounds: { 
            value: player.total_rebounds || player.rebounds || '0', 
            odds: '-' 
          },
          assists: { 
            value: player.assists || '0', 
            odds: '-' 
          },
          threePointers: { 
            value: player.three_points_made_attempted?.split('-')[0] || '0', 
            odds: '-' 
          },
          pra: (
            Number(player.points || 0) + 
            Number(player.total_rebounds || player.rebounds || 0) + 
            Number(player.assists || 0)
          ).toFixed(2),
          turnovers: { 
            value: player.turnovers || '0', 
            odds: '-' 
          },
          blocks: { 
            value: player.blocks || '0', 
            odds: '-' 
          },
          steals: { 
            value: player.steals || '0', 
            odds: '-' 
          }
        }))

        this.projections = [...team1Players, ...team2Players]
      } catch (error) {
        this.error = error.message || 'Failed to fetch projections'
        console.error('Error fetching projections:', error)
        this.projections = []
      } finally {
        this.loading = false
      }
    },

    async updateProjection(id, data) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.put(`/projections/${id}`, data)
        const index = this.projections.findIndex(p => p.id === id)
        if (index !== -1) {
          this.projections[index] = response.data
        }
      } catch (error) {
        this.error = error.message || 'Failed to update projection'
        console.error('Error updating projection:', error)
      } finally {
        this.loading = false
      }
    },

    async createProjection(data) {
      this.loading = true
      this.error = null
      try {
        const response = await axios.post('/projections', data)
        this.projections.push(response.data)
      } catch (error) {
        this.error = error.message || 'Failed to create projection'
        console.error('Error creating projection:', error)
      } finally {
        this.loading = false
      }
    },

    async deleteProjection(id) {
      this.loading = true
      this.error = null
      try {
        await axios.delete(`/projections/${id}`)
        this.projections = this.projections.filter(p => p.id !== id)
      } catch (error) {
        this.error = error.message || 'Failed to delete projection'
        console.error('Error deleting projection:', error)
      } finally {
        this.loading = false
      }
    },

    // Get market value helper
    getMarketValue(player, marketType) {
      const fieldMap = {
        points: 'points_per_30',
        total_rebounds: 'total_rebounds_per_30',
        assists: 'assists_per_30',
        three_point_field_goal: 'three_point_field_goal_per_30',
        pra: 'pra_per_30',
        turnovers: 'turnovers_per_30',
        blocks: 'blocks_per_30',
        steals: 'steals_per_30',
        ra: 'ra_per_30',
        bs: 'bs_per_30',
        pr: 'pr_per_30',
        pa: 'pa_per_30'
      }
      
      const fieldName = fieldMap[marketType]
      return fieldName ? (player[fieldName] || '0') : '0'
    },

    async getComparableDataViaWebSocket(player, market, fixtureId) {
      // Import userStore to get current user ID and token
      const { useUserStore } = await import('./userStore.js')
      const userStore = useUserStore()
      
      this.sendWebSocketMessage({
        type: 'get_comparable_data',
        player_id: player.player.id,
        market_type: market.id || market.type,
        fixture_id: fixtureId || this.projectionsData.fixture_id,
        user_id: userStore.currentUser?.id, // Add user ID for authentication
        authorization: userStore.token ? `Bearer ${userStore.token}` : null, // Add Bearer token
      })
    },



    async getComparableDataWithUrl(player, market, fixtureId, allMarketLines = false, forceUpdate = true) {
    //  return false;
      // console.log('comparable data testing', player, market.id)
      try {
        const marketType = market.type || market.id || market
        const playerId = resolveComparablePlayerId(player)
        if (playerId == null) {
          console.error('getComparableDataWithUrl: missing player id', player)
          throw new Error('Missing player id for comparable data')
        }

        this.triggeredIDs = this.triggeredIDs.filter(
          (item) => !(sameComparablePlayerId(item.player_id, playerId) && item.market_type === marketType)
        )

        const newurl = apiBalancedProjectionsPath('balanced-lines/select-comparable-data-or-player-line')
        const response = await axios.post(newurl, {
          player_id: playerId,
          market: marketType,
          force_update: forceUpdate,
          player_name: resolveComparablePlayerName(player),
          all_market_lines: allMarketLines,
          fixture_id: fixtureId || this.projectionsData.fixture_id,
        })

        const pwm = this.playersWithMarkets.find(
          (item) => sameComparablePlayerId(item.player_id, playerId) && item.market_type === marketType
        )
        if (pwm) pwm.is_loading = false

        const mergedComparableLines = mergeFixturePlayerLinesWithClientOverrides(
          response.data.allLines || [],
          response.data.clientOverrideLines || []
        )
        const hasComparableLinePayload =
          (Array.isArray(response.data.allLines) && response.data.allLines.length > 0) ||
          (Array.isArray(response.data.clientOverrideLines) && response.data.clientOverrideLines.length > 0) ||
          mergedComparableLines.length > 0

        if (hasComparableLinePayload) {
          this.balancedLinesProbabilitiesAndOdds = this.balancedLinesProbabilitiesAndOdds.filter(
            (item) => !(item.market_type == marketType && sameComparablePlayerId(item.player_id, playerId))
          )
        }

        this.allData = { ...response.data, allLines: mergedComparableLines }

        if (mergedComparableLines.length > 0) {
          this.balancedLinesProbabilitiesAndOdds.push(...mergedComparableLines)
          this.retainScorableBalancedLines()
        }

        const existingFilterIndex = this.marketFilters.findIndex(filter => 
          filter.market_type === response.data.filters.market_type && 
          filter.player_id === response.data.filters.player_id && 
          filter.fixture_id === response.data.filters.fixture_id &&
          filter.filters === response.data.filters.filters
        )


        
        if (existingFilterIndex === -1) {
          this.marketFilters.push(response.data.filters)
        } else {
          this.marketFilters[existingFilterIndex] = response.data.filters
        }

        // Check if entry already exists for this player
        const existingIndexFilters = this.balancedLinesMetadata.findIndex(
          item => item.player_id === response.data.balancedLine.player_id
        )
        
        if (existingIndexFilters !== -1) {
          // Update existing entry
          this.balancedLinesMetadata[existingIndexFilters] = response.data.balancedLine
        } else {
          // Add new entry
          this.balancedLinesMetadata.push(response.data.balancedLine)
        }
        // Update player market counters
        // console.log('market', market)
        const existingIndex = this.playerMarketCounters.findIndex(
          (item) => sameComparablePlayerId(item.player_id, playerId) && item.market_id === marketType
        )
        
        if (existingIndex !== -1) {
          this.playerMarketCounters[existingIndex].counter = response.data.comparableDataCount
        } else {
          this.playerMarketCounters.push({
            player_id: playerId,
            market_id: marketType,
            counter: response.data.comparableDataCount
          })
        }

        // Update debug data
        const existingDebugIndex = this.debug_player_market_data.findIndex(
          (item) => sameComparablePlayerId(item.player_id, playerId) && item.market_id === marketType
        )
        
        if (existingDebugIndex !== -1) {
          this.debug_player_market_data[existingDebugIndex].debug = response.data.query_debug
        } else {
          this.debug_player_market_data.push({
            player_id: playerId,
            market_id: marketType,
            debug: response.data.query_debug
          })
        }

        return this.allData
      } catch (error) {
        const marketType = market.type || market.id || market
        const pid = resolveComparablePlayerId(player)
        const entry = this.playersWithMarkets.find(
          (item) => sameComparablePlayerId(item.player_id, pid) && item.market_type === marketType
        )
        if (entry) entry.is_loading = false
        console.error('Error fetching comparable data:', error)
        throw error
      }
    },
    async getComparableData(player, market, fixtureId, allMarketLines = false, forceUpdate = true) {
    //  return false;
      // console.log('comparable data testing', player, market.id)
      try {
        const marketType = market.type || market.id || market
        const playerId = resolveComparablePlayerId(player)
        if (playerId == null) {
          console.error('getComparableData: missing player id', player)
          throw new Error('Missing player id for comparable data')
        }

        this.triggeredIDs = this.triggeredIDs.filter(
          (item) => !(sameComparablePlayerId(item.player_id, playerId) && item.market_type === marketType)
        )
        const url = this.selectedComparableDataUrl

        const response = await axios.post(url, {
          player_id: playerId,
          market: marketType,
          force_update: forceUpdate,
          player_name: resolveComparablePlayerName(player),
          all_market_lines: allMarketLines,
          fixture_id: fixtureId || this.projectionsData.fixture_id,
        })
        const mergedComparableLines = mergeFixturePlayerLinesWithClientOverrides(
          response.data.allLines || [],
          response.data.clientOverrideLines || []
        )
        const hasComparableLinePayload =
          (Array.isArray(response.data.allLines) && response.data.allLines.length > 0) ||
          (Array.isArray(response.data.clientOverrideLines) && response.data.clientOverrideLines.length > 0) ||
          mergedComparableLines.length > 0

        if (hasComparableLinePayload) {
          this.balancedLinesProbabilitiesAndOdds = this.balancedLinesProbabilitiesAndOdds.filter(
            (item) => !(item.market_type == marketType && sameComparablePlayerId(item.player_id, playerId))
          )
        }

        this.allData = { ...response.data, allLines: mergedComparableLines }

        if (mergedComparableLines.length > 0) {
          this.balancedLinesProbabilitiesAndOdds.push(...mergedComparableLines)
          this.retainScorableBalancedLines()
        }

        const existingFilterIndex = this.marketFilters.findIndex(filter => 
          filter.market_type === response.data.filters.market_type && 
          filter.player_id === response.data.filters.player_id && 
          filter.fixture_id === response.data.filters.fixture_id &&
          filter.filters === response.data.filters.filters
        )


        
        if (existingFilterIndex === -1) {
          this.marketFilters.push(response.data.filters)
        } else {
          this.marketFilters[existingFilterIndex] = response.data.filters
        }

        // Check if entry already exists for this player
        const existingIndexFilters = this.balancedLinesMetadata.findIndex(
          item => item.player_id === response.data.balancedLine.player_id
        )
        
        if (existingIndexFilters !== -1) {
          // Update existing entry
          this.balancedLinesMetadata[existingIndexFilters] = response.data.balancedLine
        } else {
          // Add new entry
          this.balancedLinesMetadata.push(response.data.balancedLine)
        }
        // Update player market counters
        // console.log('market', market)
        const existingIndex = this.playerMarketCounters.findIndex(
          (item) => sameComparablePlayerId(item.player_id, playerId) && item.market_id === marketType
        )
        
        if (existingIndex !== -1) {
          this.playerMarketCounters[existingIndex].counter = response.data.comparableDataCount
        } else {
          this.playerMarketCounters.push({
            player_id: playerId,
            market_id: marketType,
            counter: response.data.comparableDataCount
          })
        }

        // Update debug data
        const existingDebugIndex = this.debug_player_market_data.findIndex(
          (item) => sameComparablePlayerId(item.player_id, playerId) && item.market_id === marketType
        )
        
        if (existingDebugIndex !== -1) {
          this.debug_player_market_data[existingDebugIndex].debug = response.data.query_debug
        } else {
          this.debug_player_market_data.push({
            player_id: playerId,
            market_id: marketType,
            debug: response.data.query_debug
          })
        }

        return this.allData
      } catch (error) {
        const marketType = market.type || market.id || market
        const pid = resolveComparablePlayerId(player)
        const entry = this.playersWithMarkets.find(
          (item) => sameComparablePlayerId(item.player_id, pid) && item.market_type === marketType
        )
        if (entry) entry.is_loading = false
        console.error('Error fetching comparable data:', error)
        throw error
      }
    },

    feedPricingKey(playerId, marketType) {
      return `${playerId}:${marketType}`
    },
    feedPricingFor(playerId, marketType) {
      return this.feedPricingMarkets.find((row) => (
        String(row.player_id) === String(playerId) && row.market_type === marketType
      )) || null
    },
    async fetchFeedPricingState(fixtureId) {
      try {
        const response = await axios.get(apiBalancedProjectionsPath('feed-pricing/state'), {
          params: { fixture_id: fixtureId },
        })
        this.feedPricingMarkets = Array.isArray(response.data?.markets) ? response.data.markets : []
      } catch (error) {
        console.warn('Could not load feed pricing state', error)
      }
      await this.fetchFeedMode(fixtureId)
    },
    async fetchFeedMode(fixtureId) {
      try {
        const response = await axios.get(apiBalancedProjectionsPath('feed-pricing/mode'), {
          params: { fixture_id: fixtureId },
        })
        this.feedMode = response.data?.mode || null
      } catch (error) {
        this.feedMode = null
        console.warn('Could not load feed mode', error)
      }
    },
    applyFeedPricingPayload(data) {
      if (!data || typeof data !== 'object') return
      if (data.markets) {
        this.feedPricingMarkets = data.markets
      }
      if (data.state) {
        const next = data.state
        const rest = this.feedPricingMarkets.filter((row) => !(
          String(row.player_id) === String(next.player_id) && row.market_type === next.market_type
        ))
        this.feedPricingMarkets = [...rest, next]
      }
      // Never-ON / no-op OFF must not touch the grid. Incomplete payloads must
      // not replace roster rows or drop existing probability lines.
      if (data.changed === false) return
      if (Array.isArray(data.player_lines) && data.player_lines.length > 0) {
        this.mergePlayerLineUpdates(data.player_lines)
        this.retainScorableBalancedLines()
      }
      if (data.balanced_line) {
        this.updateBalancedLine(data.balanced_line)
      }
    },
    mergePlayerLineUpdates(updatedLines) {
      const next = Array.isArray(this.balancedLinesProbabilitiesAndOdds)
        ? [...this.balancedLinesProbabilitiesAndOdds]
        : []
      for (const updated of updatedLines) {
        if (!updated) continue
        const idx = next.findIndex((item) => (
          String(item.player_id) === String(updated.player_id)
          && item.market_type === updated.market_type
          && Number(item.balance_line) === Number(updated.balance_line)
        ))
        if (idx < 0) continue
        next[idx] = {
          ...next[idx],
          ...updated,
          is_balanced: updated.is_balanced ?? next[idx].is_balanced,
          player_name: updated.player_name || next[idx].player_name,
          player: updated.player || next[idx].player,
        }
      }
      this.balancedLinesProbabilitiesAndOdds = next
    },
    updateBalancedLine(balancedLine) {
      if (!balancedLine || !this.balancedLinesData?.home_team || !this.balancedLinesData?.away_team) return
      const mergeRosterRow = (player) => {
        if (String(player.player_id) !== String(balancedLine.player_id)) return player
        return {
          ...player,
          ...balancedLine,
          player: balancedLine.player || player.player,
          home_team_name: balancedLine.home_team_name || player.home_team_name,
          away_team_name: balancedLine.away_team_name || player.away_team_name,
          comparable_data: balancedLine.comparable_data ?? player.comparable_data,
          projected_minutes: balancedLine.projected_minutes ?? player.projected_minutes,
        }
      }
      this.balancedLinesData.home_team = this.balancedLinesData.home_team.map(mergeRosterRow)
      this.balancedLinesData.away_team = this.balancedLinesData.away_team.map(mergeRosterRow)
    },

    getBalancedLine(playerId, marketType) {
      return [...this.balancedLinesData.home_team, ...this.balancedLinesData.away_team].find(player => player.player_id === playerId)
    },

    toggleMonitoringToolMapping() {
      this.showMonitoringToolMapping = !this.showMonitoringToolMapping
    },

    // Cleanup method for store disposal
    cleanup() {
      this.disconnectWebSocket()
      if (this.wsReconnectInterval) {
        clearInterval(this.wsReconnectInterval)
        this.wsReconnectInterval = null
      }
    }
  }
}) 